/**
 * ContactEditorPanel — dedicated Contact editor (Phase 11.6.7). Exposes exactly the controls
 * the production Contact component (`site-builder/components/contact/Contact.tsx`, a literal
 * clone of `__reference__/realDemo/landing/components/branch`) actually renders for a single
 * location: heading, description, address (→ the map + directions link), phone/email, working
 * hours (the reference's info/hours toggle), and — when present — an action button.
 *
 * No Layout control — the component's own header comment states the reference has ONE
 * arrangement; every frozen layout (`standard`/`split`/`premium`/`mapFocus`) renders that same
 * arrangement (no per-layout differences), so there is nothing for a layout selector to do.
 *
 * No Website field — `content.contact.website` exists on the schema, but `Contact.tsx` never
 * reads it (only `phone`/`email`). No Google Map URL / Coordinates / Place ID fields — the map
 * is always derived from Address (`setBusinessField('street', …)` recomputes `location.mapUrl`
 * automatically, the same mechanism the Footer/Settings Location panel already uses); the
 * reference offers no separate way to type coordinates or a raw map URL by hand.
 *
 * Address/Phone/Email go through `setBusinessField` — the SAME persisted, businessInfo-backed
 * setter the Footer editor and Settings › Location panel use — so editing them here also
 * updates the Footer's Contact column live, and (unlike raw section-content edits) survives
 * Save/Reload.
 *
 * Social Links reuse the existing Social Links editor as-is (`SOCIAL_PLATFORM_FIELDS` +
 * `setSocial`, the same ones Settings › Social links and the Footer editor use) — one shared
 * source of truth, so a change here shows up in the Footer immediately and Save/Reload
 * persists it, with icons still detected purely from the URL (see Footer.tsx).
 *
 * Working hours UX improvement: each row (a plain `ContentItem`, `title`/`description`) is
 * still stored exactly as before — `title` is the day name, `description` is a free-form
 * "start – end" string, unchanged on the schema/Contact.tsx rendering side. Only the INPUTS
 * changed: a weekday Select for `title` (bonus: this also makes `Contact.tsx`'s "today" match
 * reliable, since it string-matches the English weekday name) and two native
 * `<input type="time">` pickers for `description` — full minute precision, no predefined
 * option list, composed back into the same "9:00 AM – 6:00 PM" string on change
 * (`splitHoursRange`/`formatHoursRange`/`to24Hour`/`to12Hour`). An existing free-typed Day
 * value that doesn't match a known weekday is never discarded — `withCustomOption` shows it
 * as its own selectable entry until the user picks a real one.
 */
import { useId } from 'react'
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { useBuilderEditor, SOCIAL_PLATFORM_FIELDS } from './editorContext'
import { Fieldset, TextField, TextArea, ToggleField } from './controls'

/** Full weekday names — matches `Contact.tsx`'s "today" highlight, which matches on the
 * English weekday name/short prefix, so picking from this list (instead of free text) also
 * makes the "today" highlight match reliably. */
const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const

const HOURS_SEPARATOR = ' – '

/** "09:15 AM" → "09:15" (24h), the value format a native `<input type="time">` expects. Empty/unparseable → ''. */
function to24Hour(time12: string): string {
  const m = /^(\d{1,2}):(\d{2})\s*([AP]M)$/i.exec(time12.trim())
  if (!m) return ''
  let h = parseInt(m[1], 10) % 12
  if (m[3].toUpperCase() === 'PM') h += 12
  return `${String(h).padStart(2, '0')}:${m[2]}`
}

/** "09:15" (24h, from the native time input) → "09:15 AM" — the same stored format as before. */
function to12Hour(time24: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(time24)
  if (!m) return ''
  const h = parseInt(m[1], 10)
  const period = h < 12 ? 'AM' : 'PM'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${String(hour12).padStart(2, '0')}:${m[2]} ${period}`
}

/** Splits a stored "9:00 AM – 6:00 PM" string back into [start, end] for the two time selects. */
function splitHoursRange(description: string): [string, string] {
  if (!description.trim()) return ['', '']
  const parts = description.split(/\s*[–—-]\s*/)
  if (parts.length === 2) return [parts[0].trim(), parts[1].trim()]
  return [description.trim(), '']
}

/** Same stored format as before — a plain "start – end" string — so rendering is unaffected. */
function formatHoursRange(start: string, end: string): string {
  if (!start && !end) return ''
  return `${start}${HOURS_SEPARATOR}${end}`
}

/** Prepends an existing value to `options` when it isn't already one of them, so editing an
 * older free-typed value never silently discards it — it just shows up as its own option
 * until the user picks a real one from the list. */
function withCustomOption(current: string, options: readonly string[]): string[] {
  return current && !options.includes(current) ? [current, ...options] : [...options]
}

/** A small labeled select — same visual language as `SelectField`, sized for two-up layout. */
function MiniSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string
  options: string[]
  onChange: (v: string) => void
}) {
  const id = useId()
  return (
    <div className="flex flex-1 flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-medium text-gray-500">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:border-gray-400 focus:outline-none"
      >
        {value === '' ? <option value="">Select…</option> : null}
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  )
}

/** A native time picker (minute precision, any time — no predefined option list) for one
 * side of a working-hours range. Stores/reads the same 12-hour "hh:mm AM/PM" string as
 * before; the `<input type="time">` element itself only ever sees the 24h value it expects. */
function MiniTimeInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const id = useId()
  return (
    <div className="flex flex-1 flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-medium text-gray-500">
        {label}
      </label>
      <input
        id={id}
        type="time"
        value={to24Hour(value)}
        onChange={(ev) => onChange(to12Hour(ev.target.value))}
        className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-sm text-gray-800 focus:border-gray-400 focus:outline-none"
      />
    </div>
  )
}

export function ContactEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const info = e.schema.businessInfo ?? {}
  const hours = section.content.items ?? []
  const actions = section.content.actions ?? []
  const lastIndex = hours.length - 1

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show Contact"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Content">
        <TextField label="Heading" value={section.content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
        <TextArea label="Description" value={section.content.subheading ?? ''} rows={3} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
      </Fieldset>

      <Fieldset title="Location">
        <TextField label="Address" value={info.street ?? ''} placeholder="123 Main St" onChange={(v) => e.setBusinessField('street', v)} />
      </Fieldset>

      <Fieldset title="Contact">
        <TextField label="Phone" value={info.phone ?? ''} placeholder="+1 555 123 4567" onChange={(v) => e.setBusinessField('phone', v)} />
        <TextField label="Email" value={info.email ?? ''} placeholder="hello@business.com" onChange={(v) => e.setBusinessField('email', v)} />
      </Fieldset>

      <Fieldset title="Working hours">
        {hours.map((h, i) => {
          const [start, end] = splitHoursRange(h.description ?? '')
          const dayOptions = withCustomOption(h.title, DAY_OPTIONS)
          return (
            <div key={h.id} className="flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50/40 p-4">
              <MiniSelect
                label="Day"
                value={h.title}
                options={dayOptions}
                onChange={(v) => e.setItemField(sectionId, h.id, 'title', v)}
              />
              <div className="flex items-end gap-2">
                <MiniTimeInput
                  label="Start time"
                  value={start}
                  onChange={(v) => e.setItemField(sectionId, h.id, 'description', formatHoursRange(v, end))}
                />
                <span className="pb-2.5 text-gray-300">→</span>
                <MiniTimeInput
                  label="End time"
                  value={end}
                  onChange={(v) => e.setItemField(sectionId, h.id, 'description', formatHoursRange(start, v))}
                />
              </div>
              <div className="mt-1 flex items-center justify-between border-t border-gray-100 pt-2.5">
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => e.moveItem(sectionId, h.id, 'up')}
                    disabled={i === 0}
                    aria-label="Move day up"
                    className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => e.moveItem(sectionId, h.id, 'down')}
                    disabled={i === lastIndex}
                    aria-label="Move day down"
                    className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => e.removeItem(sectionId, h.id)}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
              </div>
            </div>
          )
        })}
        <button
          type="button"
          onClick={() => e.addItem(sectionId)}
          className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add day
        </button>
      </Fieldset>

      <Fieldset title="Social links">
        {SOCIAL_PLATFORM_FIELDS.map((p) => (
          <TextField
            key={p.key}
            label={p.label}
            value={info.social?.[p.key] ?? ''}
            placeholder={p.placeholder}
            onChange={(v) => e.setSocial(p.key, v)}
          />
        ))}
      </Fieldset>

      {actions.length > 0 ? (
        <Fieldset title="Actions">
          {actions.map((a) => (
            <div key={a.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              <TextField label="Label" value={a.label} onChange={(v) => e.setActionField(sectionId, a.id, 'label', v)} />
              <TextField label="Link" value={a.value ?? ''} onChange={(v) => e.setActionField(sectionId, a.id, 'value', v)} />
            </div>
          ))}
        </Fieldset>
      ) : null}
    </div>
  )
}
